import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlogs = () => {
  const { getAuthHeader } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?published_only=false`, {
        headers: getAuthHeader()
      });
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    try {
      await axios.delete(`${API}/blogs/${blogId}`, {
        headers: getAuthHeader()
      });
      setBlogs(blogs.filter(blog => blog.id !== blogId));
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f]">
            Gestion des Articles
          </h1>
          <p className="text-gray-500 mt-1">{blogs.length} article(s)</p>
        </div>
        <Link to="/admin/blogs/new">
          <Button 
            className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none"
            data-testid="create-blog-btn"
          >
            <Plus className="mr-2" size={18} />
            Nouvel Article
          </Button>
        </Link>
      </div>

      {blogs.length > 0 ? (
        <div className="bg-white shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#f8f9fa] border-b">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                  Article
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                  Date
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                  Statut
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {blogs.map((blog, index) => (
                <tr key={blog.id} className="hover:bg-[#f8f9fa]" data-testid={`blog-row-${index}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img 
                        src={blog.image_url || 'https://images.unsplash.com/photo-1657129208507-7005dfc5b36a?w=100'}
                        alt={blog.title}
                        className="w-16 h-12 object-cover"
                      />
                      <div>
                        <p className="font-semibold text-[#1e3a5f]">{blog.title}</p>
                        <p className="text-gray-500 text-sm truncate max-w-xs">{blog.excerpt}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{blog.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{formatDate(blog.created_at)}</span>
                  </td>
                  <td className="px-6 py-4">
                    {blog.published ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                        <Eye size={14} /> Publié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                        <EyeOff size={14} /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/blogs/edit/${blog.id}`}>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-none"
                          data-testid={`edit-blog-${index}`}
                        >
                          <Edit size={14} />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-none"
                            data-testid={`delete-blog-${index}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. L'article "{blog.title}" sera définitivement supprimé.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Annuler</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(blog.id)}
                              className="bg-red-500 hover:bg-red-600 rounded-none"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white shadow-sm p-12 text-center">
          <FileText className="mx-auto text-gray-300 mb-4" size={48} />
          <p className="text-gray-500 mb-4">Aucun article pour le moment.</p>
          <Link to="/admin/blogs/new">
            <Button className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none">
              Créer votre premier article
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

const FileText = ({ className, size }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

export default AdminBlogs;
